import React from "react";

import FileIcon from "icons/FileIcon";
import { Meet } from "types";
import { uploadAttachment } from "util/uploadAttachment";
import ImageUpload from "./imageUpload/ImageUpload";
import { useStatus } from "util/useStatus";
import find from "lodash/find";

type UploadAttachmentProps = {
  meet: Meet;
  onComplete?: (name: string) => void;
};

const UploadAttachment = ({ meet, onComplete }: UploadAttachmentProps) => {
  const { isOnline, isSyncing } = useStatus();
  const [loading, setLoading] = React.useState(false);
  const uploadFile = React.useCallback(
    async (file: File) => {
      if (!isOnline) {
        alert(
          "Image upload is only available for online meets. Upload your meet to the cloud from the setup page before trying to upload an image.",
        );
        return;
      }

      if (!isSyncing) {
        alert(
          "You must be logged into this meet and have an active data sync connection to upload an image.",
        );
        return;
      }

      // TODO: ensure name is not already used by an attachment
      let name = prompt("Enter name for this file upload");

      const fileExt = file.name.includes(".")
        ? "." + file.name.split(".").pop()
        : "";
      name = name + fileExt;

      if (!name) {
        alert("Invalid name");
        return;
      }

      const existingAttachDocument = find(meet?.attachments, (a) => {
        return !!find(Object.keys(a?._attachments ?? {}), (at) => {
          return at === name;
        });
      });

      if (existingAttachDocument) {
        alert("An attachment with this name already exists for this meet.");
        return;
      }

      setLoading(true);
      try {
        await uploadAttachment({ meet, file, name });
      } catch (e) {
        alert("There was an error uploading your image");
      }
      setLoading(false);
      if (onComplete) {
        onComplete(name);
      }
    },
    [isOnline, isSyncing, meet, onComplete],
  );

  return (
    <ImageUpload
      buttonText={
        <span>
          <FileIcon />
          &nbsp;{loading ? "...uploading" : "Upload New Image"}
        </span>
      }
      onChange={uploadFile}
    />
  );
};

export default UploadAttachment;
